import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Polygon, Popup, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import { cellToBoundary, latLngToCell } from 'h3-js';
import { debounce } from 'lodash';
import L, { type LatLngExpression } from 'leaflet';
import { isCancel } from 'axios';
import { MapPin, Calendar, ChevronRight } from 'lucide-react';
import axios from '../api/axios';
import type { Event } from '../types/Event';
import { CATEGORIES, CATEGORY_ORDER, getEventCategory, type CategoryId } from '../utils/eventCategory';

interface HexCell {
  h3: string;
  count: number;
  location: string | null;
  categoryCounts: Partial<Record<CategoryId, number>>;
  // Set only when the cell contains exactly one event, so clicking it can
  // jump straight to that event instead of opening a breakdown popup.
  singleEvent: Event | null;
}

interface HexbinLayerProps {
  events: Event[];
  // Cells with at most this many events are excluded from the hex layer and
  // reported via `onSparseEventsChange` instead, so the parent can render
  // them as individual markers (used at the "hexagons + markers" zoom
  // tier). Defaults to 0, meaning every non-empty cell stays a hexagon.
  markerThreshold?: number;
  onSparseEventsChange?: (events: Event[]) => void;
  // Same date-range filter applied to `events`, forwarded to
  // /events/aggregate so the exact per-cell counts it returns match what's
  // actually being viewed.
  startDate?: string | null;
  endDate?: string | null;
}

// Map a Leaflet zoom level to an H3 resolution — more detail as the user
// zooms in, so the number of hexagons grows gradually.
function zoomToResolution(zoom: number): number {
  if (zoom <= 5) return 3;
  if (zoom <= 7) return 4;
  if (zoom <= 9) return 5;
  if (zoom <= 11) return 6;
  if (zoom <= 13) return 7;
  return 8;
}

// Count-based color ramp, matching the "Liczba wydarzeń" legend.
function colorForCount(count: number): string {
  if (count <= 10) return '#86efac'; // 1-10, light green
  if (count <= 50) return '#fde047'; // 10-50, yellow
  if (count <= 100) return '#fb923c'; // 50-100, orange
  if (count <= 200) return '#ef4444'; // 100-200, red
  return '#db2777'; // 200+, magenta
}

// Coarse lat/lng bucket size (degrees) used to spatially index events. ~0.5°
// is roughly 35-55km depending on latitude — small enough to give useful
// viewport culling, large enough that the index itself stays cheap to build.
const GRID_CELL_DEG = 0.5;

function gridKey(lat: number, lng: number): string {
  return `${Math.floor(lat / GRID_CELL_DEG)}:${Math.floor(lng / GRID_CELL_DEG)}`;
}

function pluralizeWydarzenie(count: number): string {
  return count === 1 ? 'wydarzenie' : count < 5 ? 'wydarzenia' : 'wydarzeń';
}

// The most frequent `location` string among a cell's events, used as a
// human-friendly label in the popup (e.g. "Śródmieście").
function dominantLocation(events: Event[]): string | null {
  const counts = new Map<string, number>();
  for (const event of events) {
    if (!event.location) continue;
    counts.set(event.location, (counts.get(event.location) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestCount = 0;
  counts.forEach((count, location) => {
    if (count > bestCount) {
      best = location;
      bestCount = count;
    }
  });
  return best;
}

export default function HexbinLayer({ events, markerThreshold = 0, onSparseEventsChange, startDate, endDate }: HexbinLayerProps) {
  const map = useMap();
  const [cells, setCells] = useState<HexCell[]>([]);
  // Authoritative per-cell data from the database (h3-pg aggregation), keyed
  // by H3 cell id. Both count and categoryCounts come from the server so the
  // hex tooltip and popup breakdown are always in sync.
  const [realData, setRealData] = useState<Map<string, { count: number; categoryCounts: Partial<Record<CategoryId, number>> }>>(new Map());
  // Holds the AbortController for the current in-flight aggregate request so
  // it can be cancelled when a newer request supersedes it.
  const aggregateAbortRef = useRef<AbortController | null>(null);

  // Bucket all events into a coarse lat/lng grid once per `events` change,
  // so panning/zooming only has to scan the (small) subset of buckets that
  // intersect the current viewport instead of iterating the full `events`
  // array on every recompute — critical once the dataset grows toward
  // ~1,000,000 records.
  const spatialIndex = useMemo(() => {
    const index = new Map<string, Event[]>();
    for (const event of events) {
      const key = gridKey(event.lat, event.lng);
      const bucket = index.get(key);
      if (bucket) {
        bucket.push(event);
      } else {
        index.set(key, [event]);
      }
    }
    return index;
  }, [events]);

  // True while a popup is open. Opening a marker popup runs a programmatic
  // centering pan (see CenterPopupOnOpen in EventsMap), which fires
  // `moveend`. Recomputing here would push a fresh `onSparseEventsChange`
  // array to the parent, rebuilding its MarkerClusterGroup layers and
  // closing the just-opened popup — the popup would visibly flicker. So we
  // pause recomputing while a popup is open and catch up once it closes.
  const popupOpenRef = useRef(false);

  // Holds the current debounced recompute function. Built inside an effect
  // (rather than useMemo) so the ref read of `popupOpenRef` only happens
  // when the debounced function actually runs (event handler/effect), never
  // during render.
  const debouncedRecomputeRef = useRef<ReturnType<typeof debounce> | null>(null);

  const recompute = useCallback(() => {
    debouncedRecomputeRef.current?.();
  }, []);

  useEffect(() => {
    const debounced = debounce(() => {
        if (popupOpenRef.current) {
          return;
        }
        const bounds = map.getBounds();
        const resolution = zoomToResolution(map.getZoom());

        // Only visit grid buckets that intersect the current viewport,
        // rather than every event in the dataset.
        const minLatIdx = Math.floor(bounds.getSouth() / GRID_CELL_DEG);
        const maxLatIdx = Math.floor(bounds.getNorth() / GRID_CELL_DEG);
        const minLngIdx = Math.floor(bounds.getWest() / GRID_CELL_DEG);
        const maxLngIdx = Math.floor(bounds.getEast() / GRID_CELL_DEG);

        // Group the events currently visible on the map into H3 cells at the
        // resolution implied by the current zoom.
        const groups = new Map<string, Event[]>();
        for (let latIdx = minLatIdx; latIdx <= maxLatIdx; latIdx++) {
          for (let lngIdx = minLngIdx; lngIdx <= maxLngIdx; lngIdx++) {
            const bucket = spatialIndex.get(`${latIdx}:${lngIdx}`);
            if (!bucket) continue;
            for (const event of bucket) {
              if (!bounds.contains([event.lat, event.lng])) continue;
              const cell = latLngToCell(event.lat, event.lng, resolution);
              const group = groups.get(cell);
              if (group) {
                group.push(event);
              } else {
                groups.set(cell, [event]);
              }
            }
          }
        }

        // Cells at/under `markerThreshold` fall back to individual markers;
        // the rest become a hexagon annotated with a category breakdown.
        const nextCells: HexCell[] = [];
        const sparseEvents: Event[] = [];

        groups.forEach((groupEvents, h3) => {
          if (groupEvents.length <= markerThreshold) {
            sparseEvents.push(...groupEvents);
            return;
          }

          const categoryCounts: Partial<Record<CategoryId, number>> = {};
          for (const event of groupEvents) {
            const category = getEventCategory(event);
            categoryCounts[category] = (categoryCounts[category] ?? 0) + 1;
          }

          nextCells.push({
            h3,
            count: groupEvents.length,
            location: dominantLocation(groupEvents),
            categoryCounts,
            singleEvent: groupEvents.length === 1 ? groupEvents[0] : null,
          });
        });

        setCells(nextCells);
        onSparseEventsChange?.(sparseEvents);

        // Fetch authoritative counts for the same cells from the database.
        // Fire-and-forget: the hex layer already renders with local counts
        // immediately, then re-colors/re-labels itself once the exact
        // numbers arrive.
        const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()].join(',');
        // Cancel any previous in-flight aggregate request to avoid stale
        // results racing back and to prevent Broken Pipe errors on the DB.
        aggregateAbortRef.current?.abort();
        const abortController = new AbortController();
        aggregateAbortRef.current = abortController;
        axios
          .get('/events/aggregate', {
            signal: abortController.signal,
            params: {
              resolution,
              bbox,
              ...(startDate ? { startDate } : {}),
              ...(endDate ? { endDate } : {}),
            },
          })
          .then((response) => {
            const cells: { h3: string; count: number; categoryCounts: Partial<Record<CategoryId, number>> }[] =
              Array.isArray(response.data?.cells) ? response.data.cells : [];
            const nextRealData = new Map<string, { count: number; categoryCounts: Partial<Record<CategoryId, number>> }>();
            for (const row of cells) {
              nextRealData.set(row.h3, { count: row.count, categoryCounts: row.categoryCounts ?? {} });
            }
            setRealData(nextRealData);
          })
          .catch((error) => {
            if (isCancel(error)) return;
            console.error('Error fetching hex aggregate counts:', error);
          });
      }, 300);
    debouncedRecomputeRef.current = debounced;
    debounced();
    return () => {
      debounced.cancel();
      debouncedRecomputeRef.current = null;
    };
  }, [map, spatialIndex, markerThreshold, onSparseEventsChange, startDate, endDate]);

  useMapEvents({
    moveend: () => recompute(),
    zoomend: () => recompute(),
    popupopen: () => {
      popupOpenRef.current = true;
    },
    popupclose: () => {
      popupOpenRef.current = false;
      // Catch up on any viewport change that happened while the popup was
      // open (e.g. the user dragged the map before closing it).
      recompute();
    },
  });

  const polygons = useMemo(
    () =>
      cells.map((cell) => {
        // h3-js returns [lat, lng] pairs, which Leaflet consumes directly.
        const boundary = cellToBoundary(cell.h3) as LatLngExpression[];
        const serverData = realData.get(cell.h3);
        // Prefer the exact server-side count when we have it; fall back to
        // the (possibly truncated) local count while it's still loading.
        const displayCount = serverData?.count ?? cell.count;
        // Use server-side categoryCounts when available so they always match
        // the authoritative count — fall back to locally-computed ones while
        // the server response is still in flight.
        const categoryCounts = serverData?.categoryCounts ?? cell.categoryCounts;
        return { cell, boundary, color: colorForCount(displayCount), displayCount, categoryCounts };
      }),
    [cells, realData]
  );

  const [hoveredCell, setHoveredCell] = useState<string | null>(null);

  return (
    <>
      {polygons.map(({ cell, boundary, color, displayCount, categoryCounts }) => {
        const breakdown = CATEGORY_ORDER
          .map(id => ({ ...CATEGORIES[id], count: categoryCounts[id] ?? 0 }))
          .filter(entry => entry.count > 0)
          .sort((a, b) => b.count - a.count);
        // Only take the "jump straight to the event" shortcut when both the
        // locally loaded data *and* the exact server count agree there's
        // really just one event in this cell — otherwise a dense cell that
        // got truncated to a single locally-loaded event could wrongly skip
        // the breakdown popup.
        const singleEvent = cell.singleEvent && displayCount <= 1 ? cell.singleEvent : null;

        return (
          <Polygon
            key={cell.h3}
            positions={boundary}
            pathOptions={{
              color: 'rgba(255,255,255,0.7)',
              weight: 1,
              fillColor: color,
              fillOpacity: hoveredCell === cell.h3 ? 0.5 : 0.25,
              className: 'hexbin-hex',
            }}
            eventHandlers={singleEvent ? {
              click: () => {
                map.setView([singleEvent.lat, singleEvent.lng], Math.max(map.getZoom() + 2, 16), { animate: true });
              },
              mouseover: (e) => {
                setHoveredCell(cell.h3);
                e.target.getTooltip()?.getElement()?.classList.add('hexbin-tooltip--hovered');
              },
              mouseout: (e) => {
                setHoveredCell(null);
                e.target.getTooltip()?.getElement()?.classList.remove('hexbin-tooltip--hovered');
              },
            } : {
              mouseover: (e) => {
                setHoveredCell(cell.h3);
                e.target.getTooltip()?.getElement()?.classList.add('hexbin-tooltip--hovered');
              },
              mouseout: (e) => {
                setHoveredCell(null);
                e.target.getTooltip()?.getElement()?.classList.remove('hexbin-tooltip--hovered');
              },
            }}
          >
            <Tooltip direction="center" permanent className="hexbin-tooltip">
              <span className="hexbin-label">{displayCount}</span>
            </Tooltip>
            {!singleEvent && (
            <Popup closeButton maxWidth={280} autoPan={false} className="hexbin-popup">
              <div className="min-w-[220px]">
                <div className="mb-3 flex items-start gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/20 text-[var(--accent)]">
                    <MapPin size={20} strokeWidth={2} aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1 pt-0.5 text-left">
                    {cell.location && (
                      <p className="truncate text-lg font-bold leading-tight text-white">{cell.location}</p>
                    )}
                    <p className="text-sm text-slate-400">{displayCount} {pluralizeWydarzenie(displayCount)}</p>
                  </div>
                </div>

                <div className="mb-3 border-t border-white/10" />

                <ul className="mb-3 flex flex-col gap-1.5">
                  {breakdown.map(entry => (
                    <li key={entry.id} className="flex items-center gap-2.5 rounded-lg border border-white/10 bg-white/5 px-3 py-2">
                      <span className="text-base" aria-hidden="true">{entry.icon}</span>
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-white">{entry.label}</span>
                      <span className="shrink-0 text-sm text-slate-300">{entry.count} {pluralizeWydarzenie(entry.count)}</span>
                      <ChevronRight size={16} className="shrink-0 text-slate-500" aria-hidden="true" />
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={() => {
                    // Close the popup ourselves first — zooming in changes
                    // the H3 resolution, so this cell's polygon (and its
                    // popup) is about to be replaced anyway. Closing it
                    // explicitly makes that a deliberate transition instead
                    // of the layer seeming to yank it away mid-recompute.
                    map.closePopup();
                    // Center the map directly on the hex (rather than
                    // fitBounds + a separate setZoom, which can leave the
                    // hex off-center once the zoom-in animation settles).
                    const center = L.latLngBounds(boundary as [number, number][]).getCenter();
                    map.setView(center, map.getZoom() + 2, { animate: true });
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--accent)] px-3 py-2.5 text-sm font-bold text-white transition hover:bg-[var(--accent-strong)]"
                >
                  <Calendar size={16} strokeWidth={2.25} aria-hidden="true" />
                  Zobacz {displayCount} {pluralizeWydarzenie(displayCount)}
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            </Popup>
            )}
          </Polygon>
        );
      })}
    </>
  );
}

